import React, { useState, useMemo } from 'react';
import { usePermissions } from '../hooks/usePermissions';
import { useTestSubLeases, TestSubLeaseFilter, TestSortConfig } from '../hooks/useTestSubLeases';
import { format } from 'date-fns';
import { ChevronUp, ChevronDown, Search, Filter, Plus, Pencil, FileText } from 'lucide-react';
import { useQueryClient } from '@tanstack/react-query';
import TestPageTable from './TestPageTable';
import { TableColumn, ColumnWidth } from '../components/common/EnhancedTable/types';
import ExpandableText from '../components/common/ExpandableText';
import { formatTextWithLinks } from '../utils/formatters';
import './TestPage.css';
import EditTestSubLeaseModal from './components/EditTestSubLeaseModal';
import AddTestSubLeaseModal from './components/AddTestSubLeaseModal';
import ViewSubLeaseLots from './components/ViewSubLeaseLots';
import { TestSublease } from '../types/test-sublease';

export default function TestPage() {
  const queryClient = useQueryClient();
  const { isAdmin, canManageSystemSettings } = usePermissions();
  
  // State for edit and add modals
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [editSublease, setEditSublease] = useState<TestSublease | null>(null);
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [isLotsModalOpen, setIsLotsModalOpen] = useState(false);
  const [selectedLotsSublease, setSelectedLotsSublease] = useState<TestSublease | null>(null);
  
  // State for filters, sorting, search, and pagination
  const [searchQuery, setSearchQuery] = useState('');
  const [isFilterOpen, setIsFilterOpen] = useState(false);
  const [filters, setFilters] = useState<TestSubLeaseFilter>({
    holder: null,
    endDateFrom: null,
    endDateTo: null,
    leaseDealing: null
  });
  const [sortConfig, setSortConfig] = useState<TestSortConfig>({
    field: 'lease_id',
    direction: 'asc'
  });
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(50);
  
  // Handle edit sublease
  const handleEditClick = (sublease: TestSublease) => {
    setEditSublease(sublease);
    setIsEditModalOpen(true);
  };
  
  // Handle edit success
  const handleEditSuccess = () => {
    queryClient.invalidateQueries({ queryKey: ['testSubleasesWithAssociatedLots'] });
  };
  
  // Handle add sublease
  const handleAddClick = () => {
    setIsAddModalOpen(true);
  };
  
  // Handle add success
  const handleAddSuccess = () => {
    queryClient.invalidateQueries({ queryKey: ['testSubleasesWithAssociatedLots'] });
  };
  
  // Handle view lots
  const handleViewLots = (sublease: TestSublease) => {
    setSelectedLotsSublease(sublease);
    setIsLotsModalOpen(true);
  };

  // Fetch data with filters and sorting
  const { data, error, isLoading } = useTestSubLeases(filters, sortConfig, searchQuery);

  // Extract unique holders for filter dropdown
  const holders = useMemo(() => {
    const holdersSet = new Set<string>();
    
    data?.forEach(sublease => {
      if (sublease.holder_name && sublease.holder_name !== 'N/A') {
        holdersSet.add(sublease.holder_name);
      }
    });
    
    return Array.from(holdersSet).sort();
  }, [data]);
  
  // Extract unique lease dealing numbers for filter dropdown
  const leaseDealings = useMemo(() => {
    const leaseDealingSet = new Set<string>();
    
    data?.forEach(sublease => {
      if (sublease.lease_dealing && sublease.lease_dealing !== 'N/A') {
        leaseDealingSet.add(sublease.lease_dealing);
      }
    });
    
    return Array.from(leaseDealingSet).sort();
  }, [data]);
  
  // Calculate unique lease dealing numbers
  const uniqueLeaseDealingCount = useMemo(() => {
    if (!data) return 0;
    
    // Create a Set to track unique lease dealing numbers
    const leaseDealingSet = new Set<string>();
    
    // Add each non-null lease dealing to the set
    data.forEach(sublease => {
      if (sublease.lease_dealing) {
        leaseDealingSet.add(sublease.lease_dealing);
      }
    });
    
    // Return the size of the set (number of unique values)
    return leaseDealingSet.size;
  }, [data]);

  // Format date function
  const formatDate = (dateString: string | null) => {
    if (!dateString) return 'N/A';
    try {
      return format(new Date(dateString), 'dd/MM/yyyy');
    } catch (e) {
      return dateString;
    }
  };

  // Handle filter changes
  const handleFilterChange = (name: keyof TestSubLeaseFilter, value: string | null) => {
    setFilters(prev => ({ ...prev, [name]: value }));
    setCurrentPage(1); // Reset to first page when filters change
  };

  // Apply pagination
  const paginatedData = useMemo(() => {
    if (!data) return [];
    
    const startIndex = (currentPage - 1) * pageSize;
    const endIndex = startIndex + pageSize;
    return data.slice(startIndex, endIndex);
  }, [data, currentPage, pageSize]);

  // Define table columns
  const columns: TableColumn<TestSublease>[] = [
    {
      id: 'edit',
      header: 'Edit',
      accessor: (row) => (
        <div className="action-buttons">
          <button
            onClick={() => handleEditClick(row)}
            className="action-button edit"
            title="Edit Sub-Lease"
          >
            <Pencil className="h-5 w-5" />
          </button>
        </div>
      ),
      width: 'narrow' as ColumnWidth,
      visibleWhen: (permissions) => permissions.isAdmin || permissions.canManageSystemSettings
    },
    {
      id: 'lots_list',
      header: 'Lots List',
      accessor: (row) => (
        <div className="action-buttons">
          <button
            onClick={() => handleViewLots(row)}
            className="action-button"
            title="View Associated Lots"
          >
            <FileText className="h-5 w-5" />
          </button>
        </div>
      ),
      width: 'narrow' as ColumnWidth,
      sortable: false
    },
    {
      id: 'associated_lots',
      header: 'Associated Lots',
      accessor: (row) => (
        <ExpandableText
          text={row.associatedLotIdsString || 'N/A'}
          maxLength={50} // Adjust as needed
          className="text-sm"
        />
      ),
      width: 'wide' as ColumnWidth, // Or 'flexible' depending on expected length
      sortable: false // Sorting a comma-separated string might not be ideal; search is primary
    },
    { 
      id: 'holder_name', 
      header: 'Name', 
      accessor: (row) => (
        <div className="truncate" title={row.holder_name || '-'}>
          {formatTextWithLinks(row.holder_name || '-')}
        </div>
      ),
      width: 'wide' as ColumnWidth,
      sortable: true
    },
    { 
      id: 'lease_start', 
      header: 'Lease Start', 
      accessor: (row) => formatDate(row.lease_start),
      width: 'medium' as ColumnWidth,
      sortable: true
    },
    { 
      id: 'lease_end', 
      header: 'Lease End', 
      accessor: (row) => formatDate(row.lease_end),
      width: 'medium' as ColumnWidth,
      sortable: true
    },
    { 
      id: 'lease_furth_term', 
      header: 'Further Term', 
      accessor: (row) => row.lease_furth_term || '-',
      width: 'medium' as ColumnWidth,
      sortable: true
    },
    { 
      id: 'lease_rent_freq', 
      header: 'Rental Frequency', 
      accessor: (row) => row.lease_rent_freq || '-',
      width: 'wide' as ColumnWidth,
      sortable: true
    },
    { 
      id: 'lease_dealing', 
      header: 'Lease Dealing No.', 
      accessor: (row) => row.lease_dealing || '-',
      width: 'wide' as ColumnWidth,
      sortable: true
    },
    { 
      id: 'lease_method', 
      header: 'Lease Method', 
      accessor: (row) => row.lease_method || '-',
      width: 'medium' as ColumnWidth,
      sortable: true
    },
    { 
      id: 'lease_type', 
      header: 'Lease Type', 
      accessor: (row) => row.lease_type || '-',
      width: 'medium' as ColumnWidth,
      sortable: true
    },
    { 
      id: 'lease_deal_doc', 
      header: 'Lease Docs', 
      accessor: (row) => (
        <ExpandableText 
          text={row.lease_deal_doc || 'Not specified'} 
          maxLength={50} 
          className="text-sm"
        />
      ),
      width: 'wide' as ColumnWidth,
      sortable: true
    },
    { 
      id: 'lease_id', 
      header: 'Lease ID', 
      accessor: (row) => row.lease_id,
      width: 'narrow' as ColumnWidth,
      sortable: true
    },
    { 
      id: 'lease_holder', 
      header: 'Leaseholder ID', 
      accessor: (row) => row.lease_holder || '-',
      width: 'medium' as ColumnWidth,
      sortable: true
    }
  ];

  // Handle pagination
  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };

  const handlePageSizeChange = (size: number) => {
    setPageSize(size);
    setCurrentPage(1); // Reset to first page when changing page size
  };

  // Calculate pagination configuration
  const paginationConfig = {
    currentPage,
    pageSize,
    totalCount: data?.length || 0,
    onPageChange: handlePageChange,
    onPageSizeChange: handlePageSizeChange,
    pageSizeOptions: [25, 50, 100, 200]
  };

  return (
    <div className="test-page-container p-4">
      <div className="test-page-header">
        <div className="space-y-4">
          <div className="flex justify-between items-center">
            <h1 className="text-2xl font-bold">Sub-Leases</h1>
            
            {/* Add button - only visible to users with admin privileges */}
            {(isAdmin || canManageSystemSettings) && (
              <button 
                onClick={handleAddClick}
                className="flex items-center px-4 py-2 bg-primary text-white rounded-md hover:bg-primary-dark"
              >
                <Plus className="h-5 w-5 mr-2" />
                Add Sub-Lease
              </button>
            )}
          </div>

          <div className="flex items-center gap-2">
            {/* Search and Filter Controls */}
            <div className="w-[300px]">
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <Search className="h-5 w-5 text-gray-400" />
                </div>
                <input
                  type="text"
                  className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                  placeholder="Search subleases..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                />
              </div>
            </div>
            
            <button
              className="flex items-center gap-2 px-4 py-2 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-md"
              onClick={() => setIsFilterOpen(!isFilterOpen)}
            >
              <Filter className="h-5 w-5" />
              Filters
            </button>
            
            <div className="flex items-center gap-4">
              <div className="text-base font-bold text-gray-700">
                Total No. of Subleases: {data?.length || 0}
              </div>
              <div className="text-base font-bold text-gray-700">
                Unique Lease Dealing No's: {uniqueLeaseDealingCount}
              </div>
            </div>
          </div>
          
          {/* Filter Panel */}
          {isFilterOpen && (
            <div className="p-4 bg-gray-50 rounded-md shadow-sm border border-gray-200">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                {/* Leaseholder Filter */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Leaseholder
                  </label>
                  <select
                    className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                    value={filters.holder || ''}
                    onChange={(e) => handleFilterChange('holder', e.target.value || null)}
                  >
                    <option value="">All Leaseholders</option>
                    {holders.map(holder => (
                      <option key={holder} value={holder}>{holder}</option>
                    ))}
                  </select>
                </div>
                
                {/* End Date From Filter */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    End Date From
                  </label>
                  <input
                    type="date"
                    className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                    value={filters.endDateFrom || ''}
                    onChange={(e) => handleFilterChange('endDateFrom', e.target.value || null)}
                  />
                </div>
                
                {/* End Date To Filter */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    End Date To
                  </label>
                  <input
                    type="date"
                    className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                    value={filters.endDateTo || ''}
                    onChange={(e) => handleFilterChange('endDateTo', e.target.value || null)}
                  />
                </div>
                
                {/* Lease Dealing No. Filter */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Lease Dealing No.
                  </label>
                  <select
                    className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                    value={filters.leaseDealing || ''}
                    onChange={(e) => handleFilterChange('leaseDealing', e.target.value || null)}
                  >
                    <option value="">All Lease Dealing Numbers</option>
                    {leaseDealings.map(dealing => (
                      <option key={dealing} value={dealing}>{dealing}</option>
                    ))}
                  </select>
                </div>
              </div>
              
              {/* Clear Filters Button */}
              <div className="mt-4 flex justify-end">
                <button
                  className="px-4 py-2 bg-gray-200 hover:bg-gray-300 text-gray-800 rounded-md"
                  onClick={() => {
                    setFilters({ holder: null, endDateFrom: null, endDateTo: null, leaseDealing: null });
                    setCurrentPage(1); // Reset to first page when clearing filters
                  }}
                >
                  Clear Filters
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
      
      <div className="test-page-table-container enhanced-table-force-scroll">
        <TestPageTable
          columns={columns}
          data={paginatedData}
          keyField="lease_id"
          width="2800px"
          maxHeight="calc(100vh - 20rem)"
          loading={isLoading}
          emptyMessage={error ? `Error: ${error.message}` : "No sublease data found"}
          horizontalScroll={true}
          alternateRowColors={true}
          pagination={paginationConfig}
          defaultSort={sortConfig}
          onSort={(newSortConfig) => {
            console.log('Sorting:', newSortConfig);
            setSortConfig(newSortConfig);
          }}
          permissions={{
            isAdmin,
            canManageSystemSettings
          }}
        />
      </div>
      
  {/* Edit Sub-Lease Modal */}
  {editSublease && (
    <EditTestSubLeaseModal
      isOpen={isEditModalOpen}
      onClose={() => {
        setIsEditModalOpen(false);
        setEditSublease(null); // Reset selected sublease when modal is closed
      }}
      onSuccess={handleEditSuccess}
      sublease={editSublease}
    />
  )}
      
      {/* Add Sub-Lease Modal */}
      <AddTestSubLeaseModal
        isOpen={isAddModalOpen}
        onClose={() => setIsAddModalOpen(false)}
        onSuccess={handleAddSuccess}
      />
      
      {/* View Sublease Lots Modal */}
      {selectedLotsSublease && (
        <ViewSubLeaseLots
          isOpen={isLotsModalOpen}
          onClose={() => {
            setIsLotsModalOpen(false);
            setSelectedLotsSublease(null);
          }}
          sublease={selectedLotsSublease}
        />
      )}
    </div>
  );
}
